/**
 * Custom web component for product pagination
 * Handles products per page dropdown and pagination navigation.
 * Uses Section Rendering API (AJAX) when changing limit to avoid full page reload.
 */
import { sectionRenderer } from '@theme/section-renderer';

class ProductPagination extends HTMLElement {
  #isDropdownOpen = false;
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    this.#cleanAddressBar();
    this.#initDropdown(signal);
    this.#initPagination(signal);
    this.#initGoToPage(signal);
  }

  /**
   * Remove section_id from the address bar so the URL stays clean (section_id is only for the Section Rendering API fetch).
   */
  #cleanAddressBar() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('section_id')) return;
    url.searchParams.delete('section_id');
    const clean = url.toString();
    if (clean !== window.location.href) {
      history.replaceState(history.state, '', clean);
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * Initialize products per page dropdown
   */
  #initDropdown(signal) {
    const button = this.querySelector('.product-pagination__dropdown-button');
    const dropdown = this.querySelector('.product-pagination__dropdown-list');
    const options = this.querySelectorAll('.product-pagination__dropdown-option');

    if (!button || !dropdown) return;

    // Handle button click
    button.addEventListener('click', (e) => {
      e.stopPropagation();
      this.#toggleDropdown();
    }, { signal });

    // Handle option clicks
    options.forEach((option) => {
      option.addEventListener('click', (e) => {
        e.stopPropagation();
        this.#handleOptionClick(option);
      }, { signal });

      // Handle keyboard navigation
      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          this.#handleOptionClick(option);
        }
      }, { signal });
    });

    // Handle keyboard navigation on button
    button.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        if (!this.#isDropdownOpen) {
          this.#openDropdown();
          // Focus first option
          const firstOption = options[0];
          if (firstOption) {
            firstOption.focus();
          }
        }
      } else if (e.key === 'Escape' && this.#isDropdownOpen) {
        e.preventDefault();
        this.#closeDropdown();
        button.focus();
      } else if (e.key === 'ArrowDown' && this.#isDropdownOpen) {
        e.preventDefault();
        const currentIndex = Array.from(options).findIndex(opt => opt === document.activeElement);
        const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
        options[nextIndex]?.focus();
      } else if (e.key === 'ArrowUp' && this.#isDropdownOpen) {
        e.preventDefault();
        const currentIndex = Array.from(options).findIndex(opt => opt === document.activeElement);
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
        options[prevIndex]?.focus();
      }
    }, { signal });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.#closeDropdown();
      }
    }, { signal });

    // Close dropdown on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#isDropdownOpen) {
        this.#closeDropdown();
        button.focus();
      }
    }, { signal });
  }

  /**
   * Initialize pagination navigation
   */
  #initPagination(signal) {
    const paginationLinks = this.querySelectorAll('.product-pagination__link');
    
    paginationLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        this.#handlePaginationClick(e, link);
      }, { signal });
    });
  }

  /**
   * Handle pagination link clicks
   * Preserves limit parameter and other query parameters in pagination URLs
   */
  #handlePaginationClick(e, link) {
    const href = link.getAttribute('href');
    if (!href) return;

    const currentUrl = new URL(window.location.href);
    const linkUrl = new URL(href, window.location.origin);
    
    // Preserve limit parameter if it exists (overwrite if link already has it)
    const currentLimit = currentUrl.searchParams.get('limit');
    if (currentLimit) {
      linkUrl.searchParams.set('limit', currentLimit);
    }
    
    // Preserve all other query parameters (except page, which is in the link URL)
    currentUrl.searchParams.forEach((value, key) => {
      if (key !== 'page' && key !== 'limit') {
        // Only add if not already present in link URL
        if (!linkUrl.searchParams.has(key)) {
          linkUrl.searchParams.set(key, value);
        }
      }
    });

    // Navigate to the new URL
    window.location.href = linkUrl.toString();
    e.preventDefault();
  }

  /**
   * “Go to page” popover opened from … ellipsis controls (collection pagination spec).
   */
  #initGoToPage(signal) {
    const numbers = this.querySelector('.main-collection__pagination-numbers');
    if (!numbers) return;

    const popover = this.querySelector('.main-collection__pagination-goto');
    const triggers = numbers.querySelectorAll('.main-collection__pagination-goto-trigger');
    const form = popover?.querySelector('.main-collection__pagination-goto-form');
    const input = form?.querySelector('input[type="number"]');

    if (!popover || triggers.length === 0 || !form || !input) return;

    const totalPages = Math.max(1, parseInt(numbers.dataset.paginationTotalPages || '1', 10));
    const limitParam = numbers.dataset.paginationLimit ?? '';

    /** @type {HTMLButtonElement | null} */
    let activeTrigger = null;

    const close = () => {
      popover.hidden = true;
      triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      activeTrigger = null;
    };

    const positionPopover = (trigger) => {
      const rect = trigger.getBoundingClientRect();
      const gap = 6;
      const width = popover.offsetWidth || 280;
      let left = rect.left + rect.width / 2 - width / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
      popover.style.position = 'fixed';
      popover.style.top = `${rect.bottom + gap}px`;
      popover.style.left = `${left}px`;
    };

    const open = (trigger) => {
      close();
      activeTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
      popover.hidden = false;
      input.removeAttribute('aria-invalid');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          positionPopover(trigger);
          input.value = '';
          input.focus();
        });
      });
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';
          if (isOpen) {
            close();
          } else {
            open(trigger);
          }
        },
        { signal },
      );
    });

    form.addEventListener(
      'submit',
      (e) => {
        e.preventDefault();
        const raw = input.value.trim();
        const n = parseInt(raw, 10);
        if (!Number.isFinite(n) || n < 1 || n > totalPages) {
          input.setAttribute('aria-invalid', 'true');
          input.focus();
          return;
        }
        input.setAttribute('aria-invalid', 'false');

        const url = new URL(window.location.href);
        url.searchParams.set('page', String(n));
        if (limitParam !== '') {
          url.searchParams.set('limit', limitParam);
        }
        window.location.assign(url.toString());
      },
      { signal },
    );

    document.addEventListener(
      'click',
      (e) => {
        if (popover.hidden) return;
        const t = /** @type {Node} */ (e.target);
        if (popover.contains(t)) return;
        if ([...triggers].some((btn) => btn.contains(t))) return;
        close();
      },
      { signal },
    );

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && !popover.hidden) {
          e.preventDefault();
          const toFocus = activeTrigger;
          close();
          toFocus?.focus();
        }
      },
      { signal },
    );

    window.addEventListener(
      'resize',
      () => {
        if (!popover.hidden && activeTrigger) positionPopover(activeTrigger);
      },
      { signal },
    );

    window.addEventListener(
      'scroll',
      () => {
        if (!popover.hidden && activeTrigger) positionPopover(activeTrigger);
      },
      { capture: true, signal },
    );
  }

  #toggleDropdown() {
    if (this.#isDropdownOpen) {
      this.#closeDropdown();
    } else {
      this.#openDropdown();
    }
  }

  #openDropdown() {
    const button = this.querySelector('.product-pagination__dropdown-button');
    const dropdown = this.querySelector('.product-pagination__dropdown-list');
    const arrow = this.querySelector('.product-pagination__dropdown-arrow');
    const options = this.querySelectorAll('.product-pagination__dropdown-option');

    if (!button || !dropdown) return;

    this.#isDropdownOpen = true;
    dropdown.classList.add('product-pagination__dropdown-list--open');
    button.classList.add('product-pagination__dropdown-button--open');
    button.setAttribute('aria-expanded', 'true');
    
    // Make options focusable
    options.forEach((option) => {
      option.setAttribute('tabindex', '0');
    });
    
    if (arrow) {
      arrow.style.transform = 'rotate(180deg)';
    }
  }

  #closeDropdown() {
    const button = this.querySelector('.product-pagination__dropdown-button');
    const dropdown = this.querySelector('.product-pagination__dropdown-list');
    const arrow = this.querySelector('.product-pagination__dropdown-arrow');
    const options = this.querySelectorAll('.product-pagination__dropdown-option');

    if (!button || !dropdown) return;

    this.#isDropdownOpen = false;
    dropdown.classList.remove('product-pagination__dropdown-list--open');
    button.classList.remove('product-pagination__dropdown-button--open');
    button.setAttribute('aria-expanded', 'false');
    
    // Make options non-focusable when closed
    options.forEach((option) => {
      option.setAttribute('tabindex', '-1');
    });
    
    if (arrow) {
      arrow.style.transform = 'rotate(0deg)';
    }
  }

  #handleOptionClick(option) {
    const value = option.dataset.value;
    const text = option.textContent.trim();

    if (value == null || value === '') return;

    // Update button text
    const buttonText = this.querySelector('.product-pagination__dropdown-button-text');
    if (buttonText) {
      buttonText.textContent = text;
    }

    // Update selected state
    const options = this.querySelectorAll('.product-pagination__dropdown-option');
    options.forEach((opt) => {
      opt.classList.remove('product-pagination__dropdown-option--selected');
      opt.setAttribute('aria-selected', 'false');
    });
    option.classList.add('product-pagination__dropdown-option--selected');
    option.setAttribute('aria-selected', 'true');

    // Close dropdown
    this.#closeDropdown();

    // Update URL
    this.#updateUrl(value);
  }

  async #updateUrl(value) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const newUrl = new URL(window.location.href);
    if (value === 'all') {
      newUrl.searchParams.set('limit', 'all');
    } else {
      const newLimit = parseInt(value, 10);
      if (Number.isNaN(newLimit)) return;
      newUrl.searchParams.set('limit', String(newLimit));
    }
    newUrl.searchParams.delete('page');

    const button = this.querySelector('.product-pagination__dropdown-button');
    const originalAriaLabel = button?.getAttribute('aria-label');

    try {
      if (button) {
        button.setAttribute('aria-label', 'Loading…');
        button.setAttribute('aria-busy', 'true');
      }
      await sectionRenderer.renderSection(sectionId, { cache: false, url: newUrl });
      newUrl.searchParams.delete('section_id');
      history.pushState({}, '', newUrl.toString());
    } catch {
      window.location.href = newUrl.toString();
      return;
    } finally {
      if (button) {
        button.removeAttribute('aria-busy');
        if (originalAriaLabel) button.setAttribute('aria-label', originalAriaLabel);
      }
    }
  }
}

// Register the custom element
if (!customElements.get('product-pagination')) {
  customElements.define('product-pagination', ProductPagination);
}
